/**
 * How meetings are named, grouped and checked in the interface.
 *
 * "Minutes pending" is not a status the server stores; it is a scheduled meeting whose end has
 * passed, which the server reports as `minutesPending`. The interface calls that a phase.
 */
import type { Attendance, MeetingStatus, MeetingSummary, WorkItemType } from "../../shared/types/api";
import type { Tone } from "../../shared/ui";

export type MeetingPhase = "UPCOMING" | "PENDING" | "COMPLETED" | "CANCELLED";

export function phaseOf(meeting: { status: MeetingStatus; minutesPending: boolean }): MeetingPhase {
  if (meeting.status === "COMPLETED") {
    return "COMPLETED";
  }
  if (meeting.status === "CANCELLED") {
    return "CANCELLED";
  }
  return meeting.minutesPending ? "PENDING" : "UPCOMING";
}

export const PHASE_LABELS: Record<MeetingPhase, string> = {
  PENDING: "Minutes pending",
  UPCOMING: "Upcoming",
  COMPLETED: "Completed",
  CANCELLED: "Cancelled",
};

export const PHASE_TONE: Record<MeetingPhase, Tone> = {
  PENDING: "red",
  UPCOMING: "blue",
  COMPLETED: "green",
  CANCELLED: "gray",
};

/** The order the tab shows groups in: the list someone should be emptying comes first. */
export const PHASE_ORDER: MeetingPhase[] = ["PENDING", "UPCOMING", "COMPLETED", "CANCELLED"];

export const PHASE_SUBTITLES: Record<MeetingPhase, string> = {
  PENDING: "These have happened. Their organisers owe the minutes.",
  UPCOMING: "Scheduled, soonest first.",
  COMPLETED: "Minutes submitted, most recent first.",
  CANCELLED: "Called off, with the reason on each.",
};

export const ATTENDANCE_LABELS: Record<Attendance, string> = {
  INVITED: "Invited",
  PRESENT: "Present",
  ABSENT: "Absent",
};

/** Types a meeting point may become. Technical task first, as the default; never a subtask. */
export const MAKEABLE_TYPES: WorkItemType[] = [
  "TECHNICAL_TASK",
  "USER_STORY",
  "BUG",
  "SPIKE",
  "ANALYSIS",
  "DEMO_PREPARATION",
  "MIGRATION",
  "SERVER_WORK",
  "EPIC",
];

export function groupMeetings<T extends MeetingSummary>(meetings: T[]): Record<MeetingPhase, T[]> {
  const groups: Record<MeetingPhase, T[]> = { PENDING: [], UPCOMING: [], COMPLETED: [], CANCELLED: [] };
  for (const meeting of meetings) {
    groups[phaseOf(meeting)].push(meeting);
  }
  const oldestFirst = (a: T, b: T) => Date.parse(a.startsAt) - Date.parse(b.startsAt);
  groups.PENDING.sort(oldestFirst);
  groups.UPCOMING.sort(oldestFirst);
  groups.COMPLETED.sort((a, b) => oldestFirst(b, a));
  groups.CANCELLED.sort((a, b) => oldestFirst(b, a));
  return groups;
}

export interface MinutesDraft {
  summary: string;
  points: { body: string }[];
  attendance: Record<string, Attendance | undefined>;
}

/** What the minutes still need, in the server's words (`MinutesCheck`). */
export function outstandingForMinutes(draft: MinutesDraft, attendeeIds: string[]): string[] {
  const missing: string[] = [];
  if (!draft.summary.trim()) {
    missing.push("a minutes summary");
  }
  if (!draft.points.some((point) => point.body.trim())) {
    missing.push("a discussion point");
  }
  const unmarked = attendeeIds.filter((id) => {
    const mark = draft.attendance[id];
    return mark !== "PRESENT" && mark !== "ABSENT";
  }).length;
  if (unmarked === 1) {
    missing.push("attendance for 1 person");
  } else if (unmarked > 1) {
    missing.push(`attendance for ${unmarked} people`);
  }
  return missing;
}

export function describeOutstanding(missing: string[]): string {
  return `Still needed before the minutes can be submitted: ${missing.join(", ")}.`;
}

/** "Tue, 23 Sep 2026, 10:00–11:00" in the viewer's own zone. */
export function formatMeetingTime(startsAt: string, endsAt: string): string {
  const start = new Date(startsAt);
  const end = new Date(endsAt);
  const day = start.toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  const time = (d: Date) => d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  if (start.toDateString() === end.toDateString()) {
    return `${day}, ${time(start)}–${time(end)}`;
  }
  const endDay = end.toLocaleDateString(undefined, { day: "numeric", month: "short" });
  return `${day}, ${time(start)} – ${endDay}, ${time(end)}`;
}

/** A wall-clock date and time, in the viewer's zone, as an ISO instant; null if incomplete. */
export function joinLocal(date: string, time: string): string | null {
  if (!date || !time) {
    return null;
  }
  const moment = new Date(`${date}T${time}`);
  return Number.isNaN(moment.getTime()) ? null : moment.toISOString();
}

/**
 * The end of a meeting as an ISO instant, given the day it starts and its two wall-clock times.
 *
 * A meeting can run past midnight — 23:30 to 00:30 — which the drawer represents as one date plus
 * two times, so an end time earlier than the start time means the end falls on the next local
 * day, not before the meeting starts. Equal times are not "the next day": they stay invalid, so
 * the caller's `endsAt > startsAt` check still refuses them.
 */
export function meetingEnd(date: string, startTime: string, endTime: string): string | null {
  if (!date || !startTime || !endTime) {
    return null;
  }
  const end = new Date(`${date}T${endTime}`);
  if (Number.isNaN(end.getTime())) {
    return null;
  }
  if (endTime < startTime) {
    end.setDate(end.getDate() + 1);
  }
  return end.toISOString();
}

const pad = (n: number) => String(n).padStart(2, "0");

/** The inverse of {@link joinLocal}: an instant as the viewer's wall-clock date and time. */
export function splitLocal(iso: string): { date: string; time: string } {
  const moment = new Date(iso);
  return {
    date: `${moment.getFullYear()}-${pad(moment.getMonth() + 1)}-${pad(moment.getDate())}`,
    time: `${pad(moment.getHours())}:${pad(moment.getMinutes())}`,
  };
}

/** Today in the viewer's zone, as YYYY-MM-DD. */
export function todayLocal(): string {
  return splitLocal(new Date().toISOString()).date;
}
